import {
    Box,
    Button,
    DialogContent,
    TextField,
    DialogTitle,
    DialogActions,
    Autocomplete,
    CircularProgress,
    Tooltip,
    Dialog,
} from '@mui/material';
import colors from 'assets/theme/base/colors';
import pxToRem from 'assets/theme/function/px-to-rem';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import { useAppDispatch } from 'hooks/redux';
import { Controller, useForm } from 'react-hook-form';
import rgba from 'assets/theme/function/rgba';
import DialogIconClose from 'component/dialog-icon-close';
import { NewAvatarRequest } from 'types/avatar-system/avatar-system';
import {
    useGetAvatarDataQuery,
    useEditAvatarMutation,
} from 'store/slices/cards/avatar/edit-avatar';
import SuccessSnackbarEffect from 'component/success-snackbar';
import PageviewIcon from '@mui/icons-material/Pageview';
import { useProductTitleSearchListQuery } from 'store/slices/toon-tint/toon-tint';
import ViewProduct from 'component/view-product';

interface Props {
    setOpenEditAvatarDialog: React.Dispatch<React.SetStateAction<boolean>>;
    avatarId: string;
    refetch: any;
}

const EditAvatar: React.FC<Props> = (props: Props) => {
    const { setOpenEditAvatarDialog, avatarId, refetch } = props;
    const [searchProduct, setSearchProduct] = useState('');
    const [viewProduct, setViewProduct] = useState('');
    const [openViewProductDialog, setOpenViewProductDialog] =
        React.useState(false);

    const { handleSubmit, control, reset } = useForm<NewAvatarRequest>();

    const {
        data,
        isSuccess: dataIsSuccess,
        refetch: refetchEditAvatar,
    } = useGetAvatarDataQuery(avatarId, {
        refetchOnMountOrArgChange: true,
    });

    const { data: dataProductToonTint, isFetching } =
        useProductTitleSearchListQuery({
            productType: 'avatar',
            searchText: searchProduct,
        });

    const [editAvatar, { error, isError, isSuccess, isLoading }] =
        useEditAvatarMutation();

    const formRef = useRef<HTMLFormElement>(null);

    const dispatch = useAppDispatch();

    const onSubmit = (values: NewAvatarRequest) => {
        editAvatar({ ...values, avatarId: avatarId });
    };

    const closeModal = () => {
        setOpenEditAvatarDialog(false);
        reset();
    };

    useEffect(() => {
        if (dataIsSuccess && data) {
            reset({
                avatarName: data.avatarName ?? '',
                productId: data.productId ?? '',
            });
            setViewProduct(data.productId ?? '');
        }
    }, [dataIsSuccess, data]);

    useEffect(() => {
        if (isSuccess) {
            setOpenEditAvatarDialog(false);
            refetch();
            refetchEditAvatar();
        }
    }, [isSuccess]);

    useEffect(() => {
        if (error && 'data' in error) {
            dispatch(
                openSnackbar({
                    message: error?.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
    }, [isError]);

    return (
        <>
            <Box
                display={'flex'}
                justifyContent={'center'}
                bgcolor={rgba(colors.primary.main, 0.5)}
            >
                <DialogTitle textTransform={'uppercase'}>
                    EDIT AVATAR
                </DialogTitle>
                <DialogIconClose handleClose={closeModal} />
            </Box>

            <DialogContent>
                {dataIsSuccess && (
                    <Box
                        ref={formRef}
                        component="form"
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        <Box
                            pt={pxToRem(15)}
                            display={'flex'}
                            justifyContent={'space-between'}
                            alignItems={'center'}
                        >
                            <Controller
                                name="avatarName"
                                control={control}
                                rules={{ required: 'Name is required' }}
                                render={({
                                    field: { name, value, onChange },
                                    fieldState: { error },
                                }) => (
                                    <TextField
                                        sx={{ width: '50%' }}
                                        name={name}
                                        value={value}
                                        onChange={onChange}
                                        label="Avatar Name"
                                        error={!!error}
                                        helperText={error?.message || ''}
                                    />
                                )}
                            />
                            <Controller
                                name="productId"
                                control={control}
                                render={({
                                    field: { onChange, ref, value },
                                }) => (
                                    <Autocomplete
                                        limitTags={5}
                                        disableCloseOnSelect
                                        sx={{
                                            width: viewProduct ? '40%' : '50%',
                                            ml: pxToRem(10),
                                        }}
                                        options={dataProductToonTint ?? []}
                                        loading={isFetching}
                                        value={
                                            (dataProductToonTint ?? []).find(
                                                (opt) => opt.id === value,
                                            ) ?? null
                                        }
                                        onChange={(_, newValue) => {
                                            onChange(
                                                newValue ? newValue.id : '',
                                            );
                                            setViewProduct(
                                                newValue ? newValue.id : '',
                                            );
                                        }}
                                        //inputValue={searchProduct}
                                        onInputChange={(
                                            _,
                                            newInput,
                                            reason,
                                        ) => {
                                            if (reason !== 'reset') {
                                                setSearchProduct(newInput);
                                            }
                                        }}
                                        getOptionLabel={(option) => {
                                            if (typeof option === 'string')
                                                return option;
                                            return option?.title ?? '';
                                        }}
                                        isOptionEqualToValue={(option, value) =>
                                            option.id === value.id
                                        }
                                        filterSelectedOptions
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                inputRef={ref}
                                                label="Product"
                                                variant="outlined"
                                                fullWidth
                                                slotProps={{
                                                    input: {
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {isFetching && (
                                                                    <CircularProgress
                                                                        color="inherit"
                                                                        size={
                                                                            20
                                                                        }
                                                                    />
                                                                )}
                                                                {
                                                                    params
                                                                        .InputProps
                                                                        .endAdornment
                                                                }
                                                            </>
                                                        ),
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                )}
                            />

                            {viewProduct && (
                                <Box
                                    pl={pxToRem(10)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <Tooltip title="View Product">
                                        <PageviewIcon
                                            sx={{ color: colors.common.white }}
                                            fontSize="large"
                                            onClick={() =>
                                                setOpenViewProductDialog(true)
                                            }
                                        />
                                    </Tooltip>
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button
                    sx={{ width: '50%' }}
                    onClick={() => formRef.current?.requestSubmit()}
                    variant={'contained'}
                    loading={isLoading}
                >
                    Edit Avatar
                </Button>
                <Button
                    sx={{ width: '50%', bgcolor: colors.action.menuSelect }}
                    onClick={() => closeModal()}
                    variant={'contained'}
                >
                    Cancel
                </Button>
            </DialogActions>

            <Dialog
                open={openViewProductDialog}
                onClose={() => setOpenViewProductDialog(false)}
                slotProps={{
                    paper: {
                        sx: {
                            width: '800px',
                            minHeight: '600px',
                            maxWidth: '90%',
                        },
                    },
                }}
            >
                <ViewProduct
                    setOpenViewProductDialog={setOpenViewProductDialog}
                    viewProduct={viewProduct}
                />
            </Dialog>

            <SuccessSnackbarEffect
                isSuccess={isSuccess}
                message={'Successfully Edit an Avatar!'}
            />
        </>
    );
};
export default EditAvatar;
